<template>
  <div class="policies">
    <div class="table">
      <div class="table__header box box--default">
        <div class="table__label table__label--medium">{{ $t(`mandatesTable.${"type"}`) }}</div>
        <div class="table__label table__label--medium">{{ $t(`mandatesTable.${"agent"}`) }}</div>
        <div class="table__label table__label--medium">{{ $t(`mandatesTable.${"noticeAgent"}`) }}</div>
        <div class="table__label table__label--medium">{{ $t(`mandatesTable.${"startedDate"}`) }}</div>
        <div class="table__label table__label--medium">{{ $t(`mandatesTable.${"signedDate"}`) }}</div>
        <div class="table__label table__label--large table__label--more-horizontal table__label--last"></div>
      </div>
      <Loading v-if="mandatesLoading" />
      <div v-else class="table__body">
        <div v-if="!mandates.length" class="table__no-data">
          <NoData :type="'mandates'" />
        </div>
        <div v-else class="table__policies">
          <div v-for="mandate in mandates" :key="mandate.id" class="policy">
            <div class="row row--policy box box--default">
              <div class="row__data row__data--medium row__data">
                {{ mandate.mandate_type ? mandate.mandate_type.name : '-----' }}
              </div>
              <div class="row__data row__data--medium row__data">
                <template v-if="mandate.agent">
                  {{ mandate.agent.first_name }} {{ mandate.agent.last_name }}
                  <span v-if="mandate.agent.company_name">
                    ({{ mandate.agent.company_name }})
                  </span>
                </template>
                <template v-else>-----</template>
              </div>
              <div class="row__data row__data--medium row__data">
                <template v-if="mandate.notice_agent">
                  {{ mandate.notice_agent.first_name }} {{ mandate.notice_agent.last_name }}
                  <span v-if="mandate.notice_agent.company_name">
                    ({{ mandate.notice_agent.company_name }})
                  </span>
                </template>
                <template v-else>-----</template>
              </div>
              <div class="row__data row__data--medium row__data mandate-date centered-row">
                <template v-if="mandate.created_at">
                  {{ formatDate(mandate.created_at) }}
                </template>
                <template v-else>-----</template>
              </div>
              <div class="row__data row__data--medium row__data mandate-date centered-row">
                <template v-if="mandate.signed_date">
                  {{ formatDate(mandate.signed_date) }}
                </template>
                <template v-else>-----</template>
              </div>


              <div class="row__data row__data--large row__data--half row__data--last">
                <div v-if="mandate.is_signed && mandate.is_signed_by_agent" @click="downloadMandate(mandate.id)"
                  class="row__data row__data--small row__data--download"
                  :class="{ 'icon--downloading': downloadingId === mandate.id }"
                  :title="downloadingId === mandate.id ? 'Downloading...' : 'Download Mandate'"></div>

                <div v-if="mandate.is_signed && mandate.is_signed_by_agent" @click="openMandateEmailModal(mandate.id)"
                  class="row__data row__data--small row__data--email" :title="'Send Email'">
                </div>

                <div v-if="
                  mandate.is_signed &&
                  mandate.is_signed_by_agent &&
                  mandate.companyDeliveries &&
                  mandate.companyDeliveries.length > 0
                " @click="toggleDeliveriesExpand(mandate.id)" class="row__data row__data--small row__data--more"
                  :class="{ 'row__data--rotate': expandedDeliveries[mandate.id] }"></div>
              </div>
            </div>

            <!-- Signing status messages -->
            <div v-if="!mandate.is_signed || !mandate.is_signed_by_agent" class="signing-status-messages">
              <div v-if="!mandate.is_signed" class="status-message status-message--warning">
                <span class="status-icon">⚠</span>
                <span class="status-text">{{ $t('mandatesTable.notSignedByCustomer') }}</span>
              </div>
              <div v-if="!mandate.is_signed_by_agent" class="status-message status-message--warning">
                <span class="status-icon">⚠</span>
                <span class="status-text">{{ $t('mandatesTable.notSignedByAgent') }}</span>
              </div>
            </div>

            <!-- Company Deliveries Section -->
            <div v-if="mandate.companyDeliveries && mandate.companyDeliveries.length > 0" class="policy__services"
              :class="{ 'policy__services--expand': expandedDeliveries[mandate.id] }">
              <div class="services" :ref="`services-${mandate.id}`" :style="expandedDeliveries[mandate.id]
                ? { maxHeight: `${calculatedDeliveriesHeights[mandate.id]}px` }
                : { maxHeight: 0 }
                " :class="{ 'services--expand': expandedDeliveries[mandate.id] }">
                <div class="table__header table__header--inner">
                  <div class="table__label table__label--medium">Company Name</div>
                  <div class="table__label table__label--medium">Email</div>
                  <div class="table__label table__label--medium">Policy Number</div>
                  <div class="table__label table__label--medium">Sender</div>
                  <div class="table__label table__label--medium">Status</div>
                  <div class="table__label table__label--medium">Sent Date</div>
                </div>
                <div>
                  <div v-for="(expandedRow, idx) in mandate.companyDeliveries" :key="`${expandedRow.id}-${idx}`"
                    class="policy-service" :class="`delivery--status--${expandedRow.status.id}`">
                    <div class="policy-service__row">
                      <div class="policy-service__data delivery-company">{{ expandedRow.company_name }}</div>
                      <div class="policy-service__data">{{ expandedRow.recipient_email }}</div>
                      <div class="policy-service__data">
                        <span v-if="expandedRow.policy_number">
                          {{ expandedRow.policy_number }}
                        </span>
                        <span v-else>-----</span>
                      </div>
                      <div class="policy-service__data">
                        <template v-if="expandedRow.sender">
                          {{ expandedRow.sender.first_name }} {{ expandedRow.sender.last_name }}
                        </template>
                        <template v-else>-----</template>
                      </div>
                      <div class="policy-service__data centered-row row__data row__data--underline"
                        style="cursor: pointer">
                        <span @click="openStatusPopup(expandedRow.id)">
                          <transition name="fade" mode="in-out">
                            <PopupEditer v-if="showStatusPopup && editingDeliveryId === expandedRow.id"
                              @togglePopup="showStatusPopup = $event" :items="statuses"
                              :selected="expandedRow.status.name" :label="'name'" @submit="updateStatus($event)" />
                          </transition>
                          {{ expandedRow.status.name }}
                        </span>
                      </div>
                      <div class="policy-service__data centered-row">
                        <template v-if="expandedRow.sent_at">
                          {{ formatDate(expandedRow.sent_at) }}
                        </template>
                        <template v-else>-----</template>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from "@/partials/Loading";
import NoData from "@/partials/NoData";
import { mapGetters } from "vuex";
import PopupEditer from "@/partials/PopupEditer";

export default {
  name: "MandatesTable",
  components: {
    Loading,
    NoData,
    PopupEditer
  },
  computed: {
    ...mapGetters(["mandates", "mandatesLoading", "customer", "mandateDeliveryStatuses"]),
    calculatedDeliveriesHeights() {
      const heights = {};
      if (this.mandates && this.mandates.length > 0) {
        this.mandates.forEach(mandate => {
          if (mandate.companyDeliveries && mandate.companyDeliveries.length > 0) {
            const headerHeight = 37;
            const rowHeight = 80;
            const totalHeight = headerHeight + (mandate.companyDeliveries.length * rowHeight);
            heights[mandate.id] = totalHeight;
          }
        });
      }
      return heights;
    }
  },
  data() {
    return {
      downloadingId: null,
      lastFetchedCustomerId: null,
      expandedDeliveries: {},
      showStatusPopup: false,
      editingDeliveryId: null,
      statuses: [],
    };
  },
  watch: {
    '$route.params.id': {
      immediate: false,
      handler(newId, oldId) {
        if (newId && oldId && newId !== oldId && this.lastFetchedCustomerId !== newId) {
          this.$store.dispatch("fetchMandates", newId).then(() => {
            this.lastFetchedCustomerId = newId;
          });
        }
      }
    }
  },
  async mounted() {
    const customerId = this.$route.params.id;

    if (this.lastFetchedCustomerId === customerId) {
      return;
    }

    await this.$store.dispatch("fetchMandates", customerId);
    await this.$store.dispatch("getMandateDeliveryStatuses");
    this.lastFetchedCustomerId = customerId;
  },
  methods: {
    formatDate(date) {
      if (!date) return '-----';
      const d = new Date(date);
      if (isNaN(d.getTime())) return date;
      const pad = (n) => String(n).padStart(2, '0');
      const day = pad(d.getDate());
      const month = pad(d.getMonth() + 1);
      const year = d.getFullYear();
      const hours = pad(d.getHours());
      const minutes = pad(d.getMinutes());
      return `${day}.${month}.${year} (${hours}:${minutes})`;
    },


    async downloadMandate(mandateId) {
      this.downloadingId = mandateId;
      try {
        const customer = (this.customer && this.customer.id)
          ? this.customer
          : { id: this.$route.params.id };

        var name = null;

        if (customer.first_name && customer.last_name) {
          name = `${customer.first_name} ${customer.last_name}`;
        } else if ((!customer.first_name || !customer.last_name) && customer.company_name) {
          name = customer.company_name;
        } else {
          name = `customer ${customer.id}`;
        }

        await this.$store.dispatch('downloadMandatePDF', {
          customerId: customer.id,
          mandateId,
          name,
        });
      } catch (error) {
        this.$store.commit("setToast", {
          display: true,
          type: "error",
          message: "Failed to download mandate.",
        });
      } finally {
        this.downloadingId = null;
      }
    },

    openMandateEmailModal(mandateId) {
      this.$store.commit("setMandateEmailModal", {
        display: true,
        mandateId,
      });
    },

    toggleDeliveriesExpand(mandateId) {
      this.$set(this.expandedDeliveries, mandateId, !this.expandedDeliveries[mandateId]);
    },

    openStatusPopup(deliveryId) {
      this.editingDeliveryId = deliveryId;
      this.statuses = this.mandateDeliveryStatuses || [];
      this.showStatusPopup = true;
    },

    async updateStatus(newStatus) {
      if (this.editingDeliveryId && newStatus) {
        this.$store.dispatch('updateDeliveryStatus', {
          deliveryId: this.editingDeliveryId,
          status: newStatus,
        }).catch(() => {
          this.$store.commit("setToast", {
            display: true,
            type: "error",
            message: "Failed to update delivery status.",
          });
        });

        const customerId = this.$route.params.id;
        await this.$store.dispatch("fetchMandates", customerId);
      }
      this.showStatusPopup = false;
      this.editingDeliveryId = null;
    },
  },
};
</script>

<style scoped lang="scss">
.centered-row {
  justify-content: center !important;
}

.mandate-actions,
.mandate-actions {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.icon__download {
  width: 24px;
  height: 24px;
  cursor: pointer;
  background: url("../../assets/icons/default/download_icon.svg") no-repeat center;
  background-size: contain;
  transition: opacity 0.2s ease;
}

.icon__download:hover:not(.icon--downloading) {
  opacity: 0.7;
}

.icon--downloading {
  opacity: 0.5;
  cursor: not-allowed;
}

.signing-status-messages {
  margin-top: 8px;
  padding: 0 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.status-message {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 13px;
}

.status-message--warning {
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  color: #856404;
}

.status-icon {
  margin-right: 8px;
  font-size: 16px;
}

.status-text {
  flex: 1;
  color: var(--text-9);
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .status-message--warning {
    background-color: rgba(255, 193, 7, 0.08);
    border: 1px solid rgba(255, 193, 7, 0.28);
    color: #ffe8a1;
  }

  .status-icon {
    color: #ffd54f;
  }

  .signing-status-messages {
    background: transparent;
  }
}

/* Support explicit dark-theme classes */
.dark-mode .status-message--warning,
.theme-dark .status-message--warning {
  background-color: rgba(255, 193, 7, 0.08);
  border: 1px solid rgba(255, 193, 7, 0.28);
  color: #ffe8a1;
}

.dark-mode .status-icon,
.theme-dark .status-icon {
  color: #ffd54f;
}

.row__data--rotate {
  transform: rotate(180deg);
  transition: transform 0.3s ease-in-out;
}

.delivery-company {
  padding-left: 10px;
}

.row__data--underline {
  color: var(--text-10) !important;
}

.policy-service__row {
  position: relative;

  &::before {
    position: absolute;
    z-index: 3;
    content: "";
    left: 0;
    top: -1px;
    height: calc(100% + 2px);
    width: 15px;
    border-top-left-radius: var(--radius-1);
    border-bottom-left-radius: var(--radius-1);
  }
}

.delivery--status--1 {
  .policy-service__row::before {
    background-color: #FF9E0F;
  }
}

.delivery--status--2 {
  .policy-service__row::before {
    background-color: #22db8e;
  }
}

.delivery--status--3 {
  .policy-service__row::before {
    background-color: #ff3333;
  }
}
</style>